import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronLeft, Mail } from "lucide-react";

const ROLES = ["member", "organiser", "admin", "super_admin"];

export default function AdminUsersPage() {
  const api = useApi();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Password reset confirmation state — we don't fire /send-reset until
  // the admin has explicitly confirmed the action, because it
  // immediately invalidates the member's current password.
  const [resetTarget, setResetTarget] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);

  const search = async (p = page) => {
    setLoading(true);
    try {
      const r = await api(`/admin/users/search?q=${encodeURIComponent(q)}&page=${p}&size=25`);
      if (r.ok) { const d = await r.json(); setUsers(d.users || []); setTotal(d.total || 0); setPage(p); }
    } finally { setLoading(false); }
  };
  useEffect(() => { search(1); /* eslint-disable-next-line */ }, []);

  const updateRole = async (u, role) => {
    const r = await api(`/admin/users/${u.user_id}`, { method: "PATCH", body: JSON.stringify({ role }) });
    if (r.ok) { toast.success("Role updated"); search(page); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Update failed"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    const r = await api(`/admin/users/${u.user_id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Deleted"); search(page); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Delete failed"); }
  };
  const sendReset = async (u) => {
    setResetBusy(true);
    try {
      const r = await api(`/admin/users/${u.user_id}/send-reset`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Temporary password emailed to ${u.email}. Valid for ${d.valid_hours || 48} hours.`);
        setResetTarget(null);
      } else {
        toast.error(d.detail || "Failed to send temporary password");
      }
    } finally {
      setResetBusy(false);
    }
  };
  const emailExport = async () => {
    const r = await api("/admin/users/export/email", { method: "POST" });
    if (r.ok) { const d = await r.json(); toast.success(`Emailed users CSV (${d.row_count} rows).`); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Failed"); }
  };

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4"><ChevronLeft size={16}/> Back</button>
      <PageHeader title="Users" subtitle="Search and manage accounts."
        actions={<Button onClick={emailExport} variant="outline" data-testid="users-email-export"><Mail size={14} className="mr-1"/> Email me all users (CSV)</Button>}
      />
      <Card className="p-5">
        <div className="flex gap-2 mb-4">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or GL Ref"
                 onKeyDown={(e) => e.key === "Enter" && search(1)} data-testid="users-search-input" />
          <Button onClick={() => search(1)} data-testid="users-search-btn">Search</Button>
        </div>
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">GL Ref</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/60">
                    <td className="py-2 pr-3">{u.name}</td>
                    <td className="py-2 pr-3">{u.email}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{u.gl_ref || "—"}</td>
                    <td className="py-2 pr-3">
                      <select className="border border-border rounded-md h-8 px-2 text-sm bg-white"
                              value={u.role} onChange={(e) => updateRole(u, e.target.value)} data-testid={`user-role-${u.user_id}`}>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-3 flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResetTarget(u)}
                        data-testid={`user-reset-${u.user_id}`}
                      >
                        Reset password
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(u)} data-testid={`user-delete-${u.user_id}`}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-between mt-3">
          <div className="text-xs text-muted-foreground">Showing {users.length} of {total}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => search(page - 1)}>Prev</Button>
            <Button size="sm" variant="ghost" disabled={users.length < 25} onClick={() => search(page + 1)}>Next</Button>
          </div>
        </div>
      </Card>

      {/* Reset-password confirmation dialog. Emphasises the two facts a
          curious admin will want to know: (a) the temp password is
          emailed straight to the member, (b) the admin doesn't see it. */}
      <AlertDialog
        open={!!resetTarget}
        onOpenChange={(o) => { if (!o && !resetBusy) setResetTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password for this member?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  Middlesex Connect will email an <strong>8-character temporary password</strong>{" "}
                  to <span className="font-medium text-foreground">{resetTarget?.email}</span>.
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>The temporary password is valid for <strong>48 hours</strong>.</li>
                  <li>You will <strong>not see</strong> the password — only the member does.</li>
                  <li>On first sign-in they&apos;ll be asked to set a new password.</li>
                  <li>Their current password stops working immediately.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetBusy} data-testid="reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (resetTarget) sendReset(resetTarget); }}
              disabled={resetBusy}
              data-testid="reset-confirm"
            >
              {resetBusy ? "Sending…" : "Email temporary password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
